import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile, toBlobURL } from '@ffmpeg/util'

let ffmpegSingleton: FFmpeg | null = null
let ffmpegLoading: Promise<FFmpeg> | null = null

async function getFfmpeg(): Promise<FFmpeg> {
  if (ffmpegSingleton) return ffmpegSingleton
  if (ffmpegLoading) return ffmpegLoading

  ffmpegLoading = (async () => {
    const ffmpeg = new FFmpeg()

    // Load core from CDN to avoid package-exports deep import restrictions.
    // If you need fully offline builds later, we can copy these assets into /public and
    // reference them from same-origin instead.
    const coreBase = 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd'

    await ffmpeg.load({
      coreURL: await toBlobURL(`${coreBase}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${coreBase}/ffmpeg-core.wasm`, 'application/wasm')
    })

    ffmpegSingleton = ffmpeg
    return ffmpeg
  })()

  return ffmpegLoading
}

function formatTime(seconds: number): string {
  // ffmpeg accepts decimals; keep a reasonable precision
  return seconds.toFixed(3)
}

export async function exportSegmentsToSingleMp4(opts: {
  sourceUrl: string
  segments: Array<{ start: number; end: number; label?: string }>
  outputFileName: string
}): Promise<Blob> {
  const { sourceUrl, segments, outputFileName } = opts

  if (!segments.length) {
    throw new Error('No segments selected')
  }

  const ffmpeg = await getFfmpeg()

  // Clean slate (best-effort)
  try {
    ffmpeg.deleteFile('input.mp4')
  } catch {}

  const inputData = await fetchFile(sourceUrl)
  await ffmpeg.writeFile('input.mp4', inputData)

  // Create trimmed parts
  const partFiles: string[] = []
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]
    const partName = `part_${i + 1}.mp4`
    partFiles.push(partName)

    // Re-encode for predictable cuts (keyframe-safe)
    await ffmpeg.exec([
      '-hide_banner',
      '-y',
      '-ss',
      formatTime(seg.start),
      '-to',
      formatTime(seg.end),
      '-i',
      'input.mp4',
      '-c:v',
      'libx264',
      '-preset',
      'veryfast',
      '-crf',
      '23',
      '-pix_fmt',
      'yuv420p',
      '-c:a',
      'aac',
      '-b:a',
      '128k',
      partName
    ])
  }

  // Concat parts into a single output
  const concatList = partFiles.map((f) => `file '${f}'`).join('\n') + '\n'
  await ffmpeg.writeFile('concat.txt', new TextEncoder().encode(concatList))

  await ffmpeg.exec([
    '-hide_banner',
    '-y',
    '-f',
    'concat',
    '-safe',
    '0',
    '-i',
    'concat.txt',
    '-c:v',
    'libx264',
    '-preset',
    'veryfast',
    '-crf',
    '23',
    '-pix_fmt',
    'yuv420p',
    '-c:a',
    'aac',
    '-b:a',
    '128k',
    'output.mp4'
  ])

  const outputData = await ffmpeg.readFile('output.mp4')
  const bytes = outputData instanceof Uint8Array ? outputData : new TextEncoder().encode(String(outputData))
  // Force an ArrayBuffer-backed copy for Blob compatibility on strict TS lib types.
  const copy = new Uint8Array(bytes)
  const blob = new Blob([copy.buffer as ArrayBuffer], { type: 'video/mp4' })

  // Best-effort cleanup
  try {
    await ffmpeg.deleteFile('concat.txt')
    await ffmpeg.deleteFile('output.mp4')
    for (const f of partFiles) await ffmpeg.deleteFile(f)
    await ffmpeg.deleteFile('input.mp4')
  } catch {}

  // Caller will handle downloading / naming
  // (We keep outputFileName for future multi-file exports)
  void outputFileName

  return blob
}
